import { useState, useEffect, useMemo } from "react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { format } from "date-fns";
import { it } from "date-fns/locale";
import ColorBarPro from "@/components/ColorBarPro";
import { 
  Palette,
  Search,
  User,
  Calendar,
  Clock,
  Check,
  Filter,
  ChevronDown,
  ChevronUp,
  Droplets,
  Sparkles,
  History,
  Eye,
  Plus,
  UserPlus,
  X,
  Package,
  Pencil,
  Trash2,
  CheckCircle2,
  Save,
  ChevronRight
} from "lucide-react";
import { Textarea } from "@/components/ui/textarea";

export default function ColorBarPage() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [trattamenti, setTrattamenti] = useState([]);
  const [trattamentiOggi, setTrattamentiOggi] = useState([]);
  const [appuntamentiColoreOggi, setAppuntamentiColoreOggi] = useState([]);
  const [operatori, setOperatori] = useState([]);
  const [clienti, setClienti] = useState([]);
  
  // Filtri
  const [searchQuery, setSearchQuery] = useState("");
  const [filterOperatore, setFilterOperatore] = useState("all");
  const [filterConfermato, setFilterConfermato] = useState("all");
  const [showFilters, setShowFilters] = useState(false);
  
  // Ricerca cliente
  const [clienteSearch, setClienteSearch] = useState("");
  const [showClienteResults, setShowClienteResults] = useState(false);
  
  // Dialog nuovo cliente
  const [newClienteOpen, setNewClienteOpen] = useState(false);
  const [newClienteData, setNewClienteData] = useState({ nome: "", cognome: "", telefono: "" });
  
  // Dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedCliente, setSelectedCliente] = useState(null);
  const [viewTrattamento, setViewTrattamento] = useState(null);
  
  // Dialog modifica trattamento
  const [editTrattamento, setEditTrattamento] = useState(null);
  const [editFormData, setEditFormData] = useState({
    formula_colore: "",
    ossigeno_volume: "",
    tempo_posa: "",
    grammatura_colore: "",
    note_tecniche: ""
  });

  // Scarico prodotto
  const [scaricoOpen, setScaricoOpen] = useState(false);
  const [prodotti, setProdotti] = useState([]);
  const [scaricoForm, setScaricoForm] = useState({
    prodotto_id: "",
    quantita: "1",
    motivo: "utilizzo",
    note: ""
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [trattamentiRes, oggiRes, appColoreRes, operatoriRes, clientiRes] = await Promise.all([
        axios.get(`${API}/colorbar/cliente/all`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/colorbar/oggi`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/colorbar/appuntamenti-colore-oggi`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] })),
        axios.get(`${API}/operatori`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/clienti`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      setTrattamentiOggi(oggiRes.data || []);
      setAppuntamentiColoreOggi(appColoreRes.data || []);
      setOperatori(operatoriRes.data?.filter(o => o.attivo) || []);
      setClienti(clientiRes.data || []);
    } catch (error) {
      console.error("Errore caricamento dati ColorBar:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTrattamentiCliente = async (clienteId) => {
    try {
      const res = await axios.get(
        `${API}/colorbar/cliente/${clienteId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return res.data || [];
    } catch (error) {
      console.error("Errore caricamento trattamenti cliente:", error);
      return [];
    }
  };

  // Fetch prodotti per scarico
  const fetchProdotti = async () => {
    try {
      const res = await axios.get(`${API}/prodotti`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProdotti(res.data || []);
    } catch (error) {
      console.error("Errore caricamento prodotti:", error);
    }
  };

  // Apri dialog scarico
  const openScaricoDialog = () => {
    fetchProdotti();
    setScaricoOpen(true);
  };

  // Scarica prodotto dal magazzino
  const handleScaricoMagazzino = async () => {
    if (!scaricoForm.prodotto_id) {
      toast.error("Seleziona un prodotto");
      return;
    }
    try {
      await axios.post(`${API}/magazzino/scarica`, {
        prodotto_id: scaricoForm.prodotto_id,
        quantita: parseFloat(scaricoForm.quantita || 1),
        motivo: scaricoForm.motivo,
        note: scaricoForm.note || `Scarico da Color Bar - ${selectedCliente ? `${selectedCliente.nome} ${selectedCliente.cognome}` : ''}`
      }, { headers: { Authorization: `Bearer ${token}` }});
      toast.success("Prodotto scaricato!");
      setScaricoOpen(false);
      setScaricoForm({ prodotto_id: "", quantita: "1", motivo: "utilizzo", note: "" });
    } catch (error) {
      toast.error("Errore nello scarico");
    }
  };

  // Ricerca clienti filtrata
  const filteredClienti = useMemo(() => {
    if (!clienteSearch || clienteSearch.length < 2) return [];
    const search = clienteSearch.toLowerCase();
    return clienti
      .filter(c => 
        `${c.nome} ${c.cognome}`.toLowerCase().includes(search) ||
        (c.telefono && c.telefono.includes(search))
      )
      .slice(0, 10);
  }, [clienteSearch, clienti]);

  // Seleziona cliente dalla ricerca
  const handleSelectCliente = async (cliente) => {
    setSelectedCliente(cliente);
    setClienteSearch(`${cliente.nome} ${cliente.cognome}`);
    setShowClienteResults(false);
    
    const storico = await fetchTrattamentiCliente(cliente.id);
    setTrattamenti(storico);
  };

  // Crea nuovo cliente
  const handleCreateCliente = async () => {
    if (!newClienteData.nome || !newClienteData.cognome) {
      toast.error("Nome e cognome sono obbligatori");
      return;
    }
    
    try {
      const res = await axios.post(
        `${API}/clienti`,
        newClienteData,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success("Cliente creato!");
      setClienti([res.data, ...clienti]);
      setNewClienteOpen(false);
      setNewClienteData({ nome: "", cognome: "", telefono: "" });
      
      // Seleziona automaticamente il nuovo cliente
      handleSelectCliente(res.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nella creazione");
    }
  };

  // Filtra i trattamenti di oggi
  const filteredTrattamenti = trattamentiOggi.filter(t => {
    const matchSearch = searchQuery === "" || 
      t.cliente_nome?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      t.formula_colore?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchOperatore = filterOperatore === "all" || t.operatore_id === filterOperatore;
    const matchConfermato = filterConfermato === "all" || 
      (filterConfermato === "confermato" && t.confermato) ||
      (filterConfermato === "bozza" && !t.confermato);
    
    return matchSearch && matchOperatore && matchConfermato;
  });

  const getOperatoreColor = (operatoreId) => {
    const op = operatori.find(o => o.id === operatoreId);
    return op?.colore || '#6366f1';
  };

  // Funzione per aprire il dialog di modifica
  const openEditDialog = (trattamento) => {
    setEditTrattamento(trattamento);
    setEditFormData({
      formula_colore: trattamento.formula_colore || "",
      ossigeno_volume: trattamento.ossigeno_volume || "",
      tempo_posa: trattamento.tempo_posa || "",
      grammatura_colore: trattamento.grammatura_colore || "",
      note_tecniche: trattamento.note_tecniche || "",
      operatore_modifica_id: trattamento.operatore_id || ""  // default: stesso operatore
    });
  };

  // Funzione per salvare le modifiche
  const saveEditTrattamento = async () => {
    if (!editTrattamento) return;
    if (!editFormData.operatore_modifica_id) {
      toast.error("Seleziona l'operatore che sta facendo la modifica");
      return;
    }
    
    const opModifica = operatori.find(o => o.id === editFormData.operatore_modifica_id);
    const opModificaNome = opModifica ? `${opModifica.nome} ${opModifica.cognome || ''}`.trim() : '';
    
    try {
      const payload = {
        formula_colore: editFormData.formula_colore,
        ossigeno_volume: editFormData.ossigeno_volume,
        tempo_posa: editFormData.tempo_posa,
        grammatura_colore: editFormData.grammatura_colore,
        note_tecniche: editFormData.note_tecniche,
        ultima_modifica_operatore_id: editFormData.operatore_modifica_id,
        ultima_modifica_operatore_nome: opModificaNome
      };
      await axios.put(`${API}/colorbar/${editTrattamento.id}`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Trattamento modificato da ${opModificaNome}`);
      setEditTrattamento(null);
      fetchData();
      if (selectedCliente) {
        handleSelectCliente(selectedCliente);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nella modifica");
    }
  };

  // Verifica se un trattamento è modificabile/eliminabile (entro 24h SEMPRE)
  const isTrattamentoModificabile = (trattamento) => {
    if (!trattamento.data_trattamento) return true;
    const dataTrattamento = new Date(trattamento.data_trattamento);
    const ora = new Date();
    const diffOre = (ora - dataTrattamento) / (1000 * 60 * 60);
    return diffOre <= 24;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-purple-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="colorbar-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-800 flex items-center gap-2" style={{ fontFamily: 'Playfair Display, serif' }}>
            <Palette className="w-6 h-6 sm:w-7 sm:h-7 text-purple-600" />
            Color Bar
          </h1>
          <p className="text-sm sm:text-base text-slate-500">Gestione e storico trattamenti colore</p>
        </div>
      </div>

      {/* Filtri SEMPRE VISIBILI in orizzontale */}
      <Card className="bg-slate-50 border-slate-200">
        <CardContent className="pt-4 pb-4">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3">
            {/* Search più grande e visibile */}
            <div className="relative flex-1 min-w-0">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                placeholder="Cerca cliente o formula nei trattamenti di oggi..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-11 h-11 bg-white border-slate-300"
                data-testid="colorbar-filter-search"
              />
            </div>

            {/* Operatore e Stato fianco a fianco anche su iPad portrait */}
            <div className="flex gap-2 sm:gap-3 sm:shrink-0">
              <Select value={filterOperatore} onValueChange={setFilterOperatore}>
                <SelectTrigger className="h-11 bg-white border-slate-300 sm:w-[180px] flex-1" data-testid="colorbar-filter-operatore">
                  <SelectValue placeholder="Operatore" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti gli operatori</SelectItem>
                  {operatori.map(op => (
                    <SelectItem key={op.id} value={op.id}>
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: op.colore }} />
                        {op.nome} {op.cognome}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={filterConfermato} onValueChange={setFilterConfermato}>
                <SelectTrigger className="h-11 bg-white border-slate-300 sm:w-[150px] flex-1" data-testid="colorbar-filter-stato">
                  <SelectValue placeholder="Stato" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti gli stati</SelectItem>
                  <SelectItem value="confermato">Confermati</SelectItem>
                  <SelectItem value="bozza">Bozze</SelectItem>
                </SelectContent>
              </Select>

              {(searchQuery || filterOperatore !== "all" || filterConfermato !== "all") && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => {
                    setSearchQuery("");
                    setFilterOperatore("all");
                    setFilterConfermato("all");
                  }}
                  className="h-11 w-11 shrink-0 text-slate-500"
                  title="Pulisci filtri"
                  data-testid="colorbar-filter-reset"
                >
                  <X className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Statistiche di oggi */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-4">
        <Card className="bg-gradient-to-br from-purple-500 to-purple-600 text-white">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <p className="text-purple-100 text-xs sm:text-sm">Trattamenti Oggi</p>
                <p className="text-2xl sm:text-3xl font-bold">{trattamentiOggi.length}</p>
              </div>
              <Palette className="w-8 h-8 sm:w-10 sm:h-10 text-purple-200 shrink-0" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-green-500 to-green-600 text-white">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <p className="text-green-100 text-xs sm:text-sm">Confermati</p>
                <p className="text-2xl sm:text-3xl font-bold">{trattamentiOggi.filter(t => t.confermato).length}</p>
              </div>
              <Check className="w-8 h-8 sm:w-10 sm:h-10 text-green-200 shrink-0" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <p className="text-amber-100 text-xs sm:text-sm">Schiariture</p>
                <p className="text-2xl sm:text-3xl font-bold">
                  {trattamentiOggi.filter(t => t.tipo_schiaritura && t.tipo_schiaritura !== "Nessuna").length}
                </p>
              </div>
              <Sparkles className="w-8 h-8 sm:w-10 sm:h-10 text-amber-200 shrink-0" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white">
          <CardContent className="pt-4">
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <p className="text-blue-100 text-xs sm:text-sm">Operatori Attivi</p>
                <p className="text-2xl sm:text-3xl font-bold">
                  {new Set(trattamentiOggi.map(t => t.operatore_id)).size}
                </p>
              </div>
              <User className="w-8 h-8 sm:w-10 sm:h-10 text-blue-200 shrink-0" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* NUOVA SEZIONE: Appuntamenti Colore di Oggi */}
      {appuntamentiColoreOggi.length > 0 && (
        <Card className="border-2 border-amber-300 bg-gradient-to-r from-amber-50 to-orange-50" data-testid="appuntamenti-colore-oggi">
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-amber-700">
                <Calendar className="w-6 h-6" />
                Appuntamenti Colore di Oggi
                <span className="ml-2 bg-amber-500 text-white px-2 py-0.5 rounded-full text-sm font-bold">
                  {appuntamentiColoreOggi.length}
                </span>
              </div>
            </CardTitle>
            <p className="text-sm text-amber-600">Clienti con servizi colore prenotati per oggi - clicca per gestire</p>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {appuntamentiColoreOggi.map((item, idx) => {
                const { appuntamento, cliente, servizi_colore, ultimo_trattamento } = item;
                const oraApp = appuntamento?.ora || '--:--';
                const clienteNome = appuntamento?.cliente_nome || `${cliente?.nome || ''} ${cliente?.cognome || ''}`.trim() || 'Cliente';
                
                return (
                  <div
                    key={appuntamento?.id || idx}
                    className="p-4 bg-white rounded-xl border-2 border-amber-200 hover:border-amber-400 hover:shadow-lg transition-all cursor-pointer group"
                    onClick={() => {
                      if (cliente) {
                        handleSelectCliente(cliente);
                      }
                    }}
                    data-testid={`appuntamento-colore-${appuntamento?.id || idx}`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center text-amber-700 font-bold group-hover:bg-amber-200 transition-colors">
                          {(clienteNome?.[0] || '?').toUpperCase()}
                        </div>
                        <div>
                          <p className="font-bold text-slate-800">{clienteNome}</p>
                          <p className="text-xs text-amber-600 flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {oraApp}
                          </p>
                        </div>
                      </div>
                      <ChevronRight className="w-5 h-5 text-amber-400 group-hover:text-amber-600 transition-colors" />
                    </div>
                    
                    {/* Servizi colore prenotati */}
                    <div className="flex flex-wrap gap-1 mb-2">
                      {servizi_colore.map((srv, sIdx) => (
                        <span 
                          key={sIdx}
                          className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full"
                        >
                          {srv}
                        </span>
                      ))}
                    </div>
                    
                    {/* Ultimo trattamento (se esiste) */}
                    {ultimo_trattamento ? (
                      <div className="p-2 bg-purple-50 rounded-lg border border-purple-100">
                        <p className="text-xs text-purple-500 mb-1 flex items-center gap-1">
                          <History className="w-3 h-3" /> Ultima formula:
                        </p>
                        <p className="text-sm font-medium text-purple-700 truncate">
                          {ultimo_trattamento.formula_colore || 'Non disponibile'}
                        </p>
                        {ultimo_trattamento.data_trattamento && (
                          <p className="text-xs text-purple-400 mt-1">
                            {format(new Date(ultimo_trattamento.data_trattamento), "d MMM yyyy", { locale: it })}
                          </p>
                        )}
                      </div>
                    ) : (
                      <div className="p-2 bg-slate-50 rounded-lg border border-dashed border-slate-200">
                        <p className="text-xs text-slate-400 flex items-center gap-1">
                          <Plus className="w-3 h-3" /> Primo trattamento colore
                        </p>
                      </div>
                    )}
                    
                    {/* Pulsante rapido */}
                    <Button
                      size="sm"
                      className="w-full mt-3 bg-amber-500 hover:bg-amber-600 text-white gap-1"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (cliente) {
                          setSelectedCliente(cliente);
                          setClienteSearch(`${cliente.nome || ''} ${cliente.cognome || ''}`.trim());
                          setDialogOpen(true);
                        }
                      }}
                      data-testid={`btn-nuovo-trattamento-${appuntamento?.id || idx}`}
                    >
                      <Palette className="w-4 h-4" />
                      {ultimo_trattamento ? 'Aggiorna Formula' : 'Crea Formula'}
                    </Button>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Ricerca Cliente - PIÙ PROMINENTE */}
      <Card className="border-2 border-purple-200 bg-gradient-to-r from-purple-50 to-white relative" style={{ zIndex: 50, overflow: 'visible' }}>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base sm:text-lg text-purple-700">
            <Search className="w-5 h-5 sm:w-6 sm:h-6" />
            Cerca Cliente per Trattamento Colore
          </CardTitle>
        </CardHeader>
        <CardContent style={{ overflow: 'visible' }}>
          <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 relative" style={{ zIndex: 60 }}>
            <div className="relative flex-1 min-w-0" style={{ zIndex: 70 }}>
              <Search className="absolute left-3 sm:left-4 top-1/2 -translate-y-1/2 w-4 h-4 sm:w-5 sm:h-5 text-purple-400" />
              <Input
                placeholder="Digita nome o telefono cliente..."
                className="pl-10 sm:pl-12 h-11 sm:h-12 text-base sm:text-lg border-2 border-purple-200 focus:border-purple-500 bg-white"
                value={clienteSearch}
                onChange={(e) => {
                  setClienteSearch(e.target.value);
                  setShowClienteResults(true);
                  if (!e.target.value) {
                    setSelectedCliente(null);
                    setTrattamenti([]);
                  }
                }}
                onFocus={() => setShowClienteResults(true)}
                data-testid="colorbar-search-cliente"
              />
              
              {/* Risultati ricerca - PIÙ VISIBILI */}
              {showClienteResults && clienteSearch.length >= 2 && filteredClienti.length > 0 && (
                <div 
                  className="absolute left-0 right-0 w-full mt-2 bg-white border-2 border-purple-300 rounded-xl shadow-2xl max-h-80 overflow-y-auto" 
                  style={{ top: '100%', zIndex: 9999 }}
                  data-testid="colorbar-search-results"
                >
                  <div className="p-2">
                    <p className="text-xs text-purple-600 font-semibold px-3 py-2 bg-purple-50 rounded-lg mb-2">
                      {filteredClienti.length} clienti trovati
                    </p>
                    {filteredClienti.map((cliente) => (
                      <button
                        key={cliente.id}
                        onClick={() => handleSelectCliente(cliente)}
                        className="w-full px-3 sm:px-4 py-2.5 sm:py-3 text-left hover:bg-purple-100 flex items-center gap-3 sm:gap-4 rounded-lg transition-colors mb-1"
                        data-testid={`colorbar-search-result-${cliente.id}`}
                      >
                        <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-purple-600 flex items-center justify-center text-white font-bold text-base sm:text-lg shrink-0">
                          {(cliente.nome?.[0] || '?').toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-bold text-slate-800 text-base sm:text-lg break-words line-clamp-1">
                            {cliente.nome} {cliente.cognome}
                          </p>
                          {cliente.telefono && (
                            <p className="text-xs sm:text-sm text-slate-500 truncate">{cliente.telefono}</p>
                          )}
                        </div>
                        <ChevronRight className="w-5 h-5 text-purple-500 shrink-0" />
                      </button>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Messaggio nessun risultato */}
              {showClienteResults && clienteSearch.length >= 2 && filteredClienti.length === 0 && (
                <div 
                  className="absolute left-0 right-0 w-full mt-2 bg-white border-2 border-purple-300 rounded-xl shadow-2xl" 
                  style={{ top: '100%', zIndex: 9999 }}
                  data-testid="colorbar-no-results"
                >
                  <div className="p-4 sm:p-6 text-center">
                    <User className="w-10 h-10 sm:w-12 sm:h-12 mx-auto text-slate-300 mb-3" />
                    <p className="text-slate-500 mb-4 text-sm sm:text-lg break-words">Nessun cliente trovato per &quot;{clienteSearch}&quot;</p>
                    <Button
                      onClick={() => {
                        setNewClienteData({ 
                          nome: clienteSearch.split(' ')[0] || '', 
                          cognome: clienteSearch.split(' ').slice(1).join(' ') || '',
                          telefono: '' 
                        });
                        setNewClienteOpen(true);
                      }}
                      className="gap-2 bg-purple-600 hover:bg-purple-700"
                      data-testid="colorbar-create-from-search"
                    >
                      <UserPlus className="w-4 h-4" />
                      Crea nuovo cliente
                    </Button>
                  </div>
                </div>
              )}
            </div>
            
            {/* Pulsante nuovo cliente */}
            <Button
              variant="outline"
              onClick={() => {
                setNewClienteData({ nome: '', cognome: '', telefono: '' });
                setNewClienteOpen(true);
              }}
              className="gap-2 h-11 sm:h-12 px-4 sm:px-6 border-2 border-purple-300 text-purple-700 hover:bg-purple-50 whitespace-nowrap shrink-0"
              data-testid="colorbar-nuovo-cliente-btn"
            >
              <Plus className="w-5 h-5" />
              <span className="hidden sm:inline">Nuovo Cliente</span>
              <span className="sm:hidden">Nuovo</span>
            </Button>
          </div>
          
          {/* Cliente selezionato con pulsante Color Bar */}
          {selectedCliente && (
            <div className="mt-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-purple-100 flex items-center justify-center">
                    <User className="w-6 h-6 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-800">
                      {selectedCliente.nome} {selectedCliente.cognome}
                    </h3>
                    {selectedCliente.telefono && (
                      <p className="text-sm text-slate-500">{selectedCliente.telefono}</p>
                    )}
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={() => {
                      setDialogOpen(true);
                    }}
                    className="gap-2 bg-purple-600 hover:bg-purple-700"
                  >
                    <Palette className="w-4 h-4" />
                    Nuovo Trattamento
                  </Button>
                  <Button
                    onClick={openScaricoDialog}
                    variant="outline"
                    className="gap-2 border-amber-300 text-amber-700 hover:bg-amber-50"
                    data-testid="colorbar-scarica-prodotto"
                  >
                    <Package className="w-4 h-4" />
                    Scarica Prodotto
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                      setSelectedCliente(null);
                      setTrattamenti([]);
                      setClienteSearch('');
                    }}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              </div>
              
              {/* Storico trattamenti */}
              {trattamenti.length > 0 ? (
                <div>
                  <p className="text-sm text-slate-500 mb-3">
                    {trattamenti.length} trattament{trattamenti.length === 1 ? 'o' : 'i'} trovato
                  </p>
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {trattamenti.map((t, idx) => (
                      <div 
                        key={t.id || idx}
                        className={`p-3 rounded-lg text-sm ${t.confermato ? 'bg-green-50' : 'bg-slate-50'}`}
                      >
                        <div className="flex justify-between items-start gap-2">
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-purple-700 break-words">{t.formula_colore}</p>
                            <p className="text-xs text-slate-500 mt-1">
                              {t.data_trattamento ? 
                                format(new Date(t.data_trattamento), "d MMM yyyy, HH:mm", { locale: it }) : 
                                "Data non disponibile"
                              }
                              {t.operatore_nome && ` • ${t.operatore_nome}`}
                            </p>
                            {t.ultima_modifica_operatore_nome && t.ultima_modifica_data && (
                              <p className="text-[11px] text-blue-600 mt-0.5">
                                ✏️ Mod. da {t.ultima_modifica_operatore_nome} il {format(new Date(t.ultima_modifica_data), "d MMM HH:mm", { locale: it })}
                              </p>
                            )}
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setViewTrattamento(t)}
                              title="Visualizza"
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            {isTrattamentoModificabile(t) && (
                              <>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => openEditDialog(t)}
                                  className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                                  title="Modifica trattamento"
                                  data-testid={`storico-edit-${t.id}`}
                                >
                                  <Pencil className="w-4 h-4" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={async () => {
                                    if (window.confirm("Sei sicuro di voler eliminare questa formula colore?")) {
                                      try {
                                        await axios.delete(`${API}/colorbar/${t.id}`, {
                                          headers: { Authorization: `Bearer ${token}` }
                                        });
                                        toast.success("Formula eliminata");
                                        if (selectedCliente) {
                                          handleSelectCliente(selectedCliente);
                                        }
                                        fetchData();
                                      } catch (error) {
                                        toast.error("Errore nell'eliminazione");
                                      }
                                    }
                                  }}
                                  className="text-red-500 hover:text-red-700 hover:bg-red-50"
                                  title="Elimina formula (entro 24h)"
                                  data-testid={`storico-delete-${t.id}`}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <p className="text-center text-slate-500 py-4">
                  Nessun trattamento colore trovato per questo cliente
                </p>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Lista trattamenti di oggi */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-purple-600" />
            Trattamenti di Oggi - {format(new Date(), "d MMMM yyyy", { locale: it })}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {filteredTrattamenti.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              <Palette className="w-12 h-12 mx-auto mb-4 text-slate-300" />
              <p>Nessun trattamento colore registrato oggi</p>
              <p className="text-sm mt-2">I trattamenti appariranno qui quando li registri dall&apos;Agenda</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredTrattamenti.map((trattamento, idx) => (
                <div 
                  key={trattamento.id || idx}
                  className={`p-4 rounded-lg border-l-4 transition-all hover:shadow-md ${
                    trattamento.confermato 
                      ? 'bg-green-50 border-l-green-500' 
                      : 'bg-slate-50 border-l-amber-500'
                  }`}
                  style={{ borderLeftColor: getOperatoreColor(trattamento.operatore_id) }}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="font-semibold text-slate-800">
                          {trattamento.cliente_nome}
                        </h3>
                        {trattamento.confermato ? (
                          <span className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded-full flex items-center gap-1">
                            <Check className="w-3 h-3" /> Confermato
                          </span>
                        ) : (
                          <span className="text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full">
                            Bozza
                          </span>
                        )}
                      </div>
                      
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <p className="text-slate-500">Formula</p>
                          <p className="font-medium text-purple-700">{trattamento.formula_colore}</p>
                        </div>
                        {trattamento.tonalizzante && (
                          <div>
                            <p className="text-slate-500">Tonalizzante</p>
                            <p className="font-medium">{trattamento.tonalizzante}</p>
                          </div>
                        )}
                        {trattamento.ossigeno_volume && (
                          <div>
                            <p className="text-slate-500">Ossigeno</p>
                            <p className="font-medium flex items-center gap-1">
                              <Droplets className="w-3 h-3 text-blue-500" />
                              {trattamento.ossigeno_volume}
                            </p>
                          </div>
                        )}
                        {trattamento.tipo_schiaritura && trattamento.tipo_schiaritura !== "Nessuna" && (
                          <div>
                            <p className="text-slate-500">Schiaritura</p>
                            <p className="font-medium flex items-center gap-1">
                              <Sparkles className="w-3 h-3 text-amber-500" />
                              {trattamento.tipo_schiaritura}
                            </p>
                          </div>
                        )}
                      </div>
                      
                      <div className="flex items-center gap-4 mt-3 text-xs text-slate-500">
                        <span className="flex items-center gap-1">
                          <User className="w-3 h-3" />
                          {trattamento.operatore_nome || 'N/A'}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" />
                          {trattamento.data_trattamento ? 
                            format(new Date(trattamento.data_trattamento), "HH:mm", { locale: it }) : 
                            "N/A"
                          }
                        </span>
                        {trattamento.tecnica_montaggio && (
                          <span>{trattamento.tecnica_montaggio}</span>
                        )}
                      </div>
                    </div>
                    
                    {/* Pulsanti azione */}
                    <div className="flex items-center gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setViewTrattamento(trattamento)}
                        className="text-slate-500 hover:text-purple-600"
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      
                      {/* Pulsanti elimina/conferma solo per trattamenti non confermati delle ultime 24h */}
                      {isTrattamentoModificabile(trattamento) && (
                        <>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditDialog(trattamento)}
                            className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                            title="Modifica trattamento"
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={async () => {
                              try {
                                await axios.put(`${API}/colorbar/${trattamento.id}/conferma`, {}, {
                                  headers: { Authorization: `Bearer ${token}` }
                                });
                                toast.success("Trattamento confermato!");
                                fetchData();
                              } catch (error) {
                                toast.error("Errore nella conferma");
                              }
                            }}
                            className="text-green-600 hover:text-green-700 hover:bg-green-50"
                            title="Conferma trattamento"
                          >
                            <CheckCircle2 className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={async () => {
                              if (window.confirm("Sei sicuro di voler eliminare questo trattamento?")) {
                                try {
                                  await axios.delete(`${API}/colorbar/${trattamento.id}`, {
                                    headers: { Authorization: `Bearer ${token}` }
                                  });
                                  toast.success("Trattamento eliminato");
                                  fetchData();
                                } catch (error) {
                                  toast.error("Errore nell'eliminazione");
                                }
                              }
                            }}
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            title="Elimina trattamento"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Dialog nuovo cliente */}
      <Dialog open={newClienteOpen} onOpenChange={setNewClienteOpen}>
        <DialogContent className="sm:max-w-md" data-testid="colorbar-nuovo-cliente-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-purple-600" />
              Nuovo Cliente
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="nome">Nome *</Label>
                <Input
                  id="nome"
                  value={newClienteData.nome}
                  onChange={(e) => setNewClienteData({...newClienteData, nome: e.target.value})}
                  placeholder="Nome"
                  data-testid="colorbar-nuovo-cliente-nome"
                />
              </div>
              <div>
                <Label htmlFor="cognome">Cognome *</Label>
                <Input
                  id="cognome"
                  value={newClienteData.cognome}
                  onChange={(e) => setNewClienteData({...newClienteData, cognome: e.target.value})}
                  placeholder="Cognome"
                  data-testid="colorbar-nuovo-cliente-cognome"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="telefono">Telefono</Label>
              <Input
                id="telefono"
                value={newClienteData.telefono}
                onChange={(e) => setNewClienteData({...newClienteData, telefono: e.target.value})}
                placeholder="Numero di telefono"
                data-testid="colorbar-nuovo-cliente-telefono"
              />
            </div>
            <div className="flex justify-end gap-2 pt-4">
              <Button variant="outline" onClick={() => setNewClienteOpen(false)} data-testid="colorbar-nuovo-cliente-annulla">
                Annulla
              </Button>
              <Button 
                onClick={handleCreateCliente}
                className="bg-purple-600 hover:bg-purple-700"
                data-testid="colorbar-nuovo-cliente-crea"
              >
                Crea Cliente
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog dettaglio trattamento */}
      {viewTrattamento && (
        <div 
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => setViewTrattamento(null)}
        >
          <div 
            className="bg-white rounded-xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold text-slate-800 flex items-center gap-2">
                <Palette className="w-5 h-5 text-purple-600" />
                Dettaglio Trattamento
              </h2>
              <button onClick={() => setViewTrattamento(null)} className="text-slate-400 hover:text-slate-600">
                ✕
              </button>
            </div>
            
            <div className="space-y-4">
              <div className="bg-purple-50 p-4 rounded-lg">
                <p className="text-sm text-purple-600">Cliente</p>
                <p className="font-semibold text-lg">{viewTrattamento.cliente_nome}</p>
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-slate-500">Formula Colore</p>
                  <p className="font-medium text-purple-700">{viewTrattamento.formula_colore}</p>
                </div>
                {viewTrattamento.tonalizzante && (
                  <div>
                    <p className="text-sm text-slate-500">Tonalizzante</p>
                    <p className="font-medium">{viewTrattamento.tonalizzante}</p>
                  </div>
                )}
                {viewTrattamento.grammatura_colore && (
                  <div>
                    <p className="text-sm text-slate-500">Grammatura</p>
                    <p className="font-medium">{viewTrattamento.grammatura_colore}</p>
                  </div>
                )}
                {viewTrattamento.ossigeno_volume && (
                  <div>
                    <p className="text-sm text-slate-500">Ossigeno</p>
                    <p className="font-medium">{viewTrattamento.ossigeno_volume}</p>
                  </div>
                )}
                {viewTrattamento.tempo_posa && (
                  <div>
                    <p className="text-sm text-slate-500">Tempo di Posa</p>
                    <p className="font-medium">{viewTrattamento.tempo_posa}</p>
                  </div>
                )}
                {viewTrattamento.tipo_schiaritura && viewTrattamento.tipo_schiaritura !== "Nessuna" && (
                  <div>
                    <p className="text-sm text-slate-500">Schiaritura</p>
                    <p className="font-medium">{viewTrattamento.tipo_schiaritura}</p>
                  </div>
                )}
                {viewTrattamento.tecnica_montaggio && (
                  <div>
                    <p className="text-sm text-slate-500">Tecnica</p>
                    <p className="font-medium">{viewTrattamento.tecnica_montaggio}</p>
                  </div>
                )}
                {viewTrattamento.zone_trattate && (
                  <div>
                    <p className="text-sm text-slate-500">Zone</p>
                    <p className="font-medium">{viewTrattamento.zone_trattate}</p>
                  </div>
                )}
              </div>
              
              <div className="border-t pt-4">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-slate-500">Operatore: {viewTrattamento.operatore_nome}</span>
                  <span className="text-slate-500">
                    {viewTrattamento.data_trattamento && 
                      format(new Date(viewTrattamento.data_trattamento), "d MMM yyyy HH:mm", { locale: it })
                    }
                  </span>
                </div>
              </div>
              
              {viewTrattamento.note_tecniche && (
                <div className="bg-slate-50 p-3 rounded-lg">
                  <p className="text-sm text-slate-500">Note Tecniche</p>
                  <p className="text-sm">{viewTrattamento.note_tecniche}</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Color Bar Dialog PRO */}
      <ColorBarPro
        cliente={selectedCliente}
        operatori={operatori}
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onSuccess={() => {
          fetchData();
          setDialogOpen(false);
        }}
      />

      {/* Dialog Scarico Prodotto */}
      <Dialog open={scaricoOpen} onOpenChange={setScaricoOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package className="w-5 h-5 text-amber-600" />
              Scarica Prodotto
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {selectedCliente && (
              <div className="p-3 bg-purple-50 rounded-lg text-sm">
                <span className="text-purple-700">Cliente: </span>
                <strong>{selectedCliente.nome} {selectedCliente.cognome}</strong>
              </div>
            )}

            <div className="space-y-2">
              <Label>Prodotto *</Label>
              <Select 
                value={scaricoForm.prodotto_id} 
                onValueChange={(v) => setScaricoForm({...scaricoForm, prodotto_id: v})}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Seleziona prodotto..." />
                </SelectTrigger>
                <SelectContent>
                  {prodotti.map(p => (
                    <SelectItem key={p.id} value={p.id}>
                      {p.nome} ({p.quantita || 0} disp.)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Quantità</Label>
                <Input
                  type="number"
                  min="1"
                  value={scaricoForm.quantita}
                  onChange={(e) => setScaricoForm({...scaricoForm, quantita: e.target.value})}
                />
              </div>
              <div className="space-y-2">
                <Label>Motivo</Label>
                <Select 
                  value={scaricoForm.motivo} 
                  onValueChange={(v) => setScaricoForm({...scaricoForm, motivo: v})}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="utilizzo">Utilizzo</SelectItem>
                    <SelectItem value="scadenza">Scadenza</SelectItem>
                    <SelectItem value="danneggiato">Danneggiato</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Note</Label>
              <Input
                value={scaricoForm.note}
                onChange={(e) => setScaricoForm({...scaricoForm, note: e.target.value})}
                placeholder="Note opzionali..."
              />
            </div>
          </div>

          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => setScaricoOpen(false)}>
              Annulla
            </Button>
            <Button onClick={handleScaricoMagazzino} className="bg-amber-600 hover:bg-amber-700">
              Scarica
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Dialog modifica trattamento */}
      <Dialog open={!!editTrattamento} onOpenChange={(open) => !open && setEditTrattamento(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil className="w-5 h-5 text-blue-600" />
              Modifica Trattamento
            </DialogTitle>
          </DialogHeader>
          
          {editTrattamento && (
            <div className="space-y-4">
              <div className="p-3 bg-purple-50 rounded-lg">
                <p className="font-medium text-purple-800">{editTrattamento.cliente_nome}</p>
                <p className="text-xs text-purple-600">
                  {editTrattamento.data_trattamento && 
                    format(new Date(editTrattamento.data_trattamento), "d MMMM yyyy, HH:mm", { locale: it })
                  }
                  {editTrattamento.operatore_nome && ` • creato da ${editTrattamento.operatore_nome}`}
                </p>
              </div>

              {/* Operatore della modifica - OBBLIGATORIO */}
              <div>
                <Label className="text-sm font-semibold text-red-600">Operatore che sta modificando *</Label>
                <Select
                  value={editFormData.operatore_modifica_id || ""}
                  onValueChange={(v) => setEditFormData({ ...editFormData, operatore_modifica_id: v })}
                >
                  <SelectTrigger className={!editFormData.operatore_modifica_id ? "border-red-300" : ""} data-testid="edit-trattamento-operatore">
                    <SelectValue placeholder="Seleziona operatore..." />
                  </SelectTrigger>
                  <SelectContent>
                    {operatori.map(op => (
                      <SelectItem key={op.id} value={op.id}>
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: op.colore }} />
                          {op.nome} {op.cognome}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-3">
                <div>
                  <Label>Formula Colore</Label>
                  <Input
                    value={editFormData.formula_colore}
                    onChange={(e) => setEditFormData({...editFormData, formula_colore: e.target.value})}
                    placeholder="Es: WELLA: 7/3 [45g + 20 vol]"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Ossigeno</Label>
                    <Input
                      value={editFormData.ossigeno_volume}
                      onChange={(e) => setEditFormData({...editFormData, ossigeno_volume: e.target.value})}
                      placeholder="Es: 20 vol"
                    />
                  </div>
                  <div>
                    <Label>Tempo Posa</Label>
                    <Input
                      value={editFormData.tempo_posa}
                      onChange={(e) => setEditFormData({...editFormData, tempo_posa: e.target.value})}
                      placeholder="Es: 35 min"
                    />
                  </div>
                </div>
                
                <div>
                  <Label>Grammatura</Label>
                  <Input
                    value={editFormData.grammatura_colore}
                    onChange={(e) => setEditFormData({...editFormData, grammatura_colore: e.target.value})}
                    placeholder="Es: 45g colore + 90g ossigeno"
                  />
                </div>
                
                <div>
                  <Label>Note Tecniche</Label>
                  <Textarea
                    value={editFormData.note_tecniche}
                    onChange={(e) => setEditFormData({...editFormData, note_tecniche: e.target.value})}
                    placeholder="Note aggiuntive..."
                    rows={3}
                  />
                </div>
              </div>
              
              <div className="flex justify-end gap-2 pt-4 border-t">
                <Button variant="outline" onClick={() => setEditTrattamento(null)}>
                  Annulla
                </Button>
                <Button onClick={saveEditTrattamento} className="bg-blue-600 hover:bg-blue-700 gap-2">
                  <Save className="w-4 h-4" />
                  Salva Modifiche
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
